import React, { useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ExternalLink, Download, FileText, CreditCard } from "lucide-react";
import { cn } from "@/lib/utils";
import { useVaultStore } from "@/stores/billing/vault-store";
import { useBillingStore } from "@/stores/billing/billing-store";

/**
 * BillingHistory
 * Invoice list from Stripe with status badges, view, and download actions.
 */

interface BillingHistoryProps {
  orgId?: string; // Deprecated: orgId now derived from org context header
}

const STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  paid: {
    label: "Paid",
    color: "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300",
  },
  open: {
    label: "Open",
    color: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
  },
  draft: {
    label: "Draft",
    color: "bg-gray-100 text-gray-700 dark:bg-gray-900/40 dark:text-gray-300",
  },
  void: {
    label: "Void",
    color: "bg-gray-100 text-gray-500 dark:bg-gray-900/40 dark:text-gray-400",
  },
  uncollectible: {
    label: "Uncollectible",
    color: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
  },
};

function formatUnixDate(timestamp: number): string {
  try {
    return new Date(timestamp * 1000).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return String(timestamp);
  }
}

function formatAmount(amountCents: number, currency: string): string {
  const dollars = amountCents / 100;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency.toUpperCase(),
  }).format(dollars);
}

export const BillingHistory: React.FC<BillingHistoryProps> = () => {
  const { invoices, isLoading, fetchInvoices } = useVaultStore();
  const { createPortalSession, isLoading: billingLoading } = useBillingStore();

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  const getStatusConfig = (status: string) => {
    return STATUS_CONFIG[status] || {
      label: status,
      color: "bg-gray-100 text-gray-700 dark:bg-gray-900/40 dark:text-gray-300",
    };
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Invoices
            </CardTitle>
            <CardDescription>
              View and download your billing invoices.
            </CardDescription>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => createPortalSession()}
            disabled={billingLoading}
            className="gap-1.5"
          >
            <CreditCard className="h-4 w-4" />
            Manage Billing
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading && invoices.length === 0 ? (
          <div className="space-y-3">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : invoices.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">
            No invoices found.
          </p>
        ) : (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Invoice #</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invoices.map((invoice) => {
                  const statusCfg = getStatusConfig(invoice.status);
                  return (
                    <TableRow key={invoice.id}>
                      <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                        {formatUnixDate(invoice.created)}
                      </TableCell>
                      <TableCell className="font-mono text-xs">
                        {invoice.number || "--"}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="secondary"
                          className={cn("text-xs", statusCfg.color)}
                        >
                          {statusCfg.label}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm font-medium text-foreground">
                        {formatAmount(invoice.amountDue, invoice.currency)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          {invoice.hostedInvoiceUrl && (
                            <Button
                              variant="ghost"
                              size="sm"
                              asChild
                              className="gap-1 text-xs"
                            >
                              <a
                                href={invoice.hostedInvoiceUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <ExternalLink className="h-3.5 w-3.5" />
                                View
                              </a>
                            </Button>
                          )}
                          {invoice.invoicePdf && (
                            <Button
                              variant="ghost"
                              size="sm"
                              asChild
                              className="gap-1 text-xs"
                            >
                              <a
                                href={invoice.invoicePdf}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <Download className="h-3.5 w-3.5" />
                                PDF
                              </a>
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
